import React, { useState, useEffect, useCallback } from "react";
import { Button, CircularProgress } from "@mui/material";
import JumboConfirmDialog from "@jumbo/components/JumboConfirmDialog";
import authServices from "app/services/auth-services";
import { PaymentStatus } from "app/utils/constants/paymentStatus";
import { ROUTES } from "app/utils/constants/routeConstants";

const EnrollButton = ({
  plan,
  isLoggedIn,
  authUser,
  navigate,
  location,
  width = "50%",
  sx,
  size = "large",
  autoTrigger = false,
  autoTriggerPlanId = null,
}) => {
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadRazorPay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handleLoginDialogOpen = () => {
    setOpenLoginDialog(true);
  };

  const handleLoginDialogClose = () => {
    setOpenLoginDialog(false);
  };

  const handleLoginConfirm = () => {
    const redirectURI = encodeURIComponent(
      `${location.pathname}?planId=${plan.id}`
    );
    const redirectUrl = `${ROUTES.USER_LOGIN}?redirectURI=${redirectURI}`;
    navigate(redirectUrl);
    handleLoginDialogClose();
  };

  const showRazorpay = useCallback(
    async (planToEnroll) => {
      if (!isLoggedIn) {
        handleLoginDialogOpen();
        return;
      }

      setLoading(true);

      let initiateOrderPaymentResponse = null;
      try {
        const res = await loadRazorPay();
        if (!res) {
          throw new Error("Failed to load Razorpay checkout script");
        }

        initiateOrderPaymentResponse = await authServices.coursePurchaseDetails(
          planToEnroll.id
        );

        if (
          !initiateOrderPaymentResponse ||
          !initiateOrderPaymentResponse.orderId
        ) {
          throw new Error("Order ID not found in the response");
        }

        const options = {
          key: "rzp_live_94YBtdwavT4Xal",
          amount: planToEnroll.price,
          currency: "INR",
          name: "CodingPal",
          description: planToEnroll.name,
          image: "https://codingpal.org/images/codingpal/codingpal.jpg",
          order_id: initiateOrderPaymentResponse.orderId,
          handler: async (response) => {
            try {
              const updateResponse =
                await authServices.updateCoursePurchaseDetails(
                  initiateOrderPaymentResponse.id,
                  {
                    id: initiateOrderPaymentResponse.id,
                    paymentStatus: PaymentStatus.PAYMENT_COMPLETED,
                    paymentId: response.razorpay_payment_id,
                    signature: response.razorpay_signature,
                    orderId: initiateOrderPaymentResponse.orderId,
                  }
                );

              if (
                updateResponse &&
                updateResponse.paymentStatus === PaymentStatus.PAYMENT_COMPLETED
              ) {
                const searchParams = new URLSearchParams({
                  paymentStatus: PaymentStatus.PAYMENT_COMPLETED,
                  planName: planToEnroll.name,
                });
                navigate(`${ROUTES.ALGOS_AND_DS}?${searchParams.toString()}`); // Success: Redirect to ALGOS_AND_DS
              } else {
                const searchParams = new URLSearchParams({
                  paymentStatus: "UNKNOWN",
                  planName: planToEnroll.name,
                });
                navigate(`${ROUTES.UPGRADE}?${searchParams.toString()}`); // Unknown status: Redirect to UPGRADE
              }
            } catch (updateErr) {
              const searchParams = new URLSearchParams({
                paymentStatus: PaymentStatus.PAYMENT_FAILED,
                planName: planToEnroll.name,
              });
              navigate(`${ROUTES.UPGRADE}?${searchParams.toString()}`); // Failure during update: Redirect to UPGRADE
              try {
                await authServices.updateCoursePurchaseDetails(
                  initiateOrderPaymentResponse.id,
                  {
                    id: initiateOrderPaymentResponse.id,
                    paymentStatus: PaymentStatus.PAYMENT_FAILED,
                  }
                );
              } catch (updateErr2) {
                console.error(
                  "Failed to update payment status to FAILED:",
                  updateErr2
                );
              }
            }
          },
          prefill: {
            email: authUser?.email || "",
          },
          theme: {
            color: "#2C3E50",
          },
        };

        const paymentObject = new window.Razorpay(options);
        paymentObject.on("payment.failed", async (response) => {
          console.error("Razorpay payment failed:", response);
          try {
            await authServices.updateCoursePurchaseDetails(
              initiateOrderPaymentResponse.id,
              {
                id: initiateOrderPaymentResponse.id,
                paymentStatus: PaymentStatus.PAYMENT_FAILED,
              }
            );

            const searchParams = new URLSearchParams({
              paymentStatus: PaymentStatus.PAYMENT_FAILED,
              planName: plan.name,
            });
            navigate(`${ROUTES.UPGRADE}?${searchParams.toString()}`); // Payment failed: Redirect to UPGRADE
          } catch (updateErr) {
            console.error(
              "Failed to update payment status to FAILED:",
              updateErr
            );
            const searchParams = new URLSearchParams({
              paymentStatus: PaymentStatus.PAYMENT_FAILED,
              planName: plan.name,
            });
            navigate(`${ROUTES.UPGRADE}?${searchParams.toString()}`); // Error during update: Redirect to UPGRADE
          }
        });

        paymentObject.open();
        setLoading(false);
      } catch (err) {
        console.error("Payment error:", err);
        const searchParams = new URLSearchParams({
          paymentStatus: "ERROR",
          planName: plan.name,
        });
        navigate(`${ROUTES.UPGRADE}?${searchParams.toString()}`); // General error: Redirect to UPGRADE
        if (initiateOrderPaymentResponse?.id) {
          try {
            await authServices.updateCoursePurchaseDetails(
              initiateOrderPaymentResponse.id,
              {
                id: initiateOrderPaymentResponse.id,
                paymentStatus: PaymentStatus.PAYMENT_FAILED,
              }
            );
          } catch (updateErr) {
            console.error(
              "Failed to update payment status to FAILED:",
              updateErr
            );
          }
        }
        setLoading(false);
      }
    },
    [isLoggedIn, authUser?.email, navigate, plan.name]
  );

  // Auto-trigger Razorpay dialog if autoTrigger is true and user is logged in
  useEffect(() => {
    if (autoTrigger && isLoggedIn && plan.id === autoTriggerPlanId) {
      showRazorpay(plan);
    }
  }, [autoTrigger, isLoggedIn, plan, autoTriggerPlanId, showRazorpay]);

  return (
    <>
      <Button
        variant="contained"
        disabled={loading}
        onClick={() => showRazorpay(plan)}
        sx={{
          textTransform: "none",
          width: width,
          ...sx,
        }}
        size={size}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {loading ? "" : "Enroll Now"}
      </Button>

      <JumboConfirmDialog
        open={openLoginDialog}
        onClose={handleLoginDialogClose}
        onConfirm={handleLoginConfirm}
        title="Login Required"
        message="Please login to enroll in this course."
        cancelText="Cancel"
        confirmText="Login"
        cancelColor="error"
        confirmColor="primary"
        confirmVariant="contained"
      />
    </>
  );
};

export default EnrollButton;
